import React, { useMemo, useState } from "react";
import { Alert, AspectRatio, Box, Loader, Modal, Stack, Text } from "@mantine/core";
import type { IAssetTarget, IBlobMedia } from "@multiconsult-group/multiplacer-core";
import { useGetMultiplacerAssetsSasQuery } from "../../openapi";

type Props = {
  media: IBlobMedia;
  target: IAssetTarget;
  maxHeight?: number;
};

export const BlobMediaPreview: React.FC<Props> = ({
  media,
  target,
  maxHeight = 280,
}) => {
  const [opened, setOpened] = useState(false);

  const queryArg = useMemo(
    () => ({
      target,
      blobPath: media.blobPath,
    }),
    [target, media.blobPath]
  );

  const { data, isLoading, isError, error } = useGetMultiplacerAssetsSasQuery(queryArg, {
    refetchOnMountOrArgChange: false,
  });

  const url = data?.url;

  if (isLoading) {
    return (
      <Box>
        <Loader size="sm" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert color="red" variant="light" title="Kunne ikke laste media">
        {(error as any)?.data?.message ?? "Ukjent feil"}
      </Alert>
    );
  }

  if (!url) {
    return (
      <Alert color="gray" variant="light" title="Ingen URL">
        Mangler SAS-url for blob.
      </Alert>
    );
  }

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={media.caption ?? "Media"}
        fullScreen
        padding="md"
        styles={{
          body: {
            height: "100%",
            overflow: "hidden", // ✅ no scroll
          },
        }}
      >
        <Stack
          gap="sm"
          style={{
            height: "calc(100vh - 80px)", // reserve space for modal header
            overflow: "hidden", // ✅ no scroll
          }}
        >
          {/* Media container takes all available space */}
          <Box
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              borderRadius: 8,
            }}
          >
            {media.kind === "image" ? (
              // eslint-disable-next-line jsx-a11y/alt-text
              <img
                src={url}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <video
                src={url}
                controls
                autoPlay
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              />
            )}
          </Box>

          {/* Caption: single line, no scroll */}
          {media.caption ? (
            <Text size="sm" c="dimmed" lineClamp={1}>
              {media.caption}
            </Text>
          ) : null}
        </Stack>
      </Modal>

      {/* Preview (clickable) */}
      {media.kind === "image" ? (
        <Stack gap={4}>
          <Box
            onClick={() => setOpened(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setOpened(true);
            }}
            style={{
              maxHeight,
              overflow: "hidden",
              borderRadius: 8,
              cursor: "zoom-in",
              outline: "none",
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <img
              src={url}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: "contain",
              }}
            />
          </Box>

          {media.caption ? (
            <Text size="xs" c="dimmed">
              {media.caption}
            </Text>
          ) : null}
        </Stack>
      ) : (
        <Stack gap={4}>
          <Box
            onClick={() => setOpened(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setOpened(true);
            }}
            style={{ cursor: "zoom-in", outline: "none" }}
          >
            <AspectRatio ratio={16 / 9}>
              <video
                src={url}
                controls
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 8,
                }}
              />
            </AspectRatio>
          </Box>

          {media.caption ? (
            <Text size="xs" c="dimmed">
              {media.caption}
            </Text>
          ) : null}
        </Stack>
      )}
    </>
  );
};
